/**
 * RouteComparisonView renders one card per candidate route into the Safety tab, so the user can compare
 * the standard route against the safety route(s) and decide whether the security is worth the detour.
 *
 * Each card shows: colour swatch (matching the polyline), label, distance, travel time, Δ distance and
 * Δ time vs. the standard route, number of secure parkings along the route, number of incidents nearby,
 * security-coverage % and the largest uncovered gap, plus a "Select" button that makes it the active
 * route on the map. Pure DOM, Bootstrap classes already used elsewhere in the project.
 */

import { formatKm, formatDuration, metricsLine, deltaLine } from "./RouteFormat";
import { metricHelp } from "../RouteHelp";

export class RouteComparisonView {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
  }

  clear() {
    if (this.container) {
      this.container.innerHTML = "";
    }
  }

  message(html) {
    if (this.container) {
      this.container.innerHTML = `<div class="alert alert-info">${html}</div>`;
    }
  }

  /**
   * Appends a footnote below the cards (e.g. the best-effort disclaimer or a "no safer route" hint).
   * @param {string} text
   * @param {string} className
   */
  appendNote(text, className) {
    if (!this.container) {
      return;
    }
    const note = document.createElement("div");
    note.className = className;
    note.textContent = text;
    this.container.appendChild(note);
  }

  /**
   * @param {Array<{label: string, color: string, score: object, isBaseline?: boolean}>} routes
   * @param {number} activeIndex
   * @param {(index: number) => void} onSelect
   */
  render(routes, activeIndex, onSelect) {
    if (!this.container) {
      return;
    }
    this.container.innerHTML = "";

    const baseline = routes.find((r) => r.isBaseline) || routes[0];

    routes.forEach((route, index) => {
      const s = route.score;
      const isActive = index === activeIndex;

      const card = document.createElement("div");
      card.className = `card mb-2 ${isActive ? "border-primary" : ""}`;
      card.style.borderLeft = `6px solid ${route.color}`;

      const body = document.createElement("div");
      body.className = "card-body p-2";

      body.innerHTML = `
        <div class="d-flex justify-content-between align-items-center">
          <strong>${route.label}</strong>
          <span>${formatKm(s.distance)} · ${formatDuration(s.duration)}${metricHelp("Route")}</span>
        </div>
        <div class="small text-muted">${deltaLine(route, baseline)}${metricHelp("Route difference")}</div>
        <div class="small mt-1">${metricsLine(s, true)}</div>
      `;

      const button = document.createElement("button");
      button.type = "button";
      button.className = `btn btn-sm mt-2 ${isActive ? "btn-primary" : "btn-outline-primary"}`;
      button.textContent = isActive ? "Selected" : "Select this route";
      button.disabled = isActive;
      button.addEventListener("click", () => onSelect(index));

      body.appendChild(button);
      card.appendChild(body);
      this.container.appendChild(card);
    });
  }
}
